/**
 * The words this site ships with, and the shape they travel in.
 *
 * Deliberately free of `server-only`, and that is the whole reason this file is
 * separate from `copy.ts`. Resolving copy means reading Neon and MP, which is
 * server work — but the RESULT crosses into client components, and so do the
 * defaults a component falls back to when nothing was passed. A client bundle
 * that imported the resolver would drag a database driver with it.
 *
 * `copy.ts` layers the two databases over these. See the note there for the
 * order and why it is that way round.
 */

/**
 * Every string this site asks for, with the words it falls back to.
 *
 * One object rather than scattered calls, for two reasons. It is the list a
 * staff member needs in order to know what they can change — a label table you
 * cannot enumerate is one nobody edits. And it means the fallbacks are all
 * visible together, so nobody has to hunt a component to find out what the
 * button currently says.
 */
export const COPY_DEFAULTS = {
  'register.cta': 'Sign up',
  'register.ctaAgain': 'Sign someone up',
  'register.heading': 'Sign up',
  'register.addGuest': 'Bring a friend',
  'register.going': 'Going',
  'register.pending': 'Not paid yet',
  'register.notRight': 'Not right?',
  'register.signPersonUp': 'Sign {name} up',
  'register.correctable': 'If that is wrong, change it here and we will fix the record.',
  'register.genderLabel': 'Gender',
  'register.birthdayLabel': 'Birthday',
  /** MP's own words for these. A form and a database disagreeing about what a
   *  field is called is how two people end up meaning different things. */
  'register.genderFemale': 'Female',
  'register.genderMale': 'Male',
  'profile.signUpSomeoneElse': 'Sign someone else up',
  'profile.household': 'Household',
  /** MP calls a Congregation a Campus at Woodside, and says so in a setting. */
  'common.congregation': 'Campus',

  /**
   * THE PUBLIC PAGE'S OWN WORDS.
   *
   * Everything above is the register and profile flows, which is where this
   * layer started. These are the sentences a visitor actually reads, and the
   * ones Misty is most likely to want changed — the prayer card in particular,
   * which is copy about consent and is the sort of thing a ministry lead
   * rewords twice before it is right.
   *
   * Not every string on the site: an aria-label nobody sees and a heading four
   * hundred women read are not the same kind of thing, and a label table with
   * two hundred rows in it is one nobody opens.
   */
  'prayer.eyebrow': 'Prayer',
  'prayer.heading': 'What can we pray for?',
  /** The button face only. Says the private part out loud before she taps,
   *  because the promise inside the textarea is no use to somebody deciding
   *  whether to open it at all. */
  'prayer.invitation': 'Private to our team, unless you say otherwise.',
  'prayer.placeholder': 'Prayer requests are private to our team.',
  'prayer.nameLabel': 'Your name (optional)',
  'prayer.emailLabel': 'Email (optional)',
  'prayer.shareable': 'This one can be shared with the room.',
  'prayer.shareableNote': 'We may read it out or put it on a screen during the weekend.',
  'prayer.shareName': 'You can use my name.',
  'prayer.shareNameNote': 'Otherwise it is shared without anything that identifies you.',
  'prayer.send': 'Send it',
  'prayer.sending': 'Sending…',
  'prayer.failed': 'That did not save. Try once more, or find the prayer team down front.',
  'prayer.thanks': 'Thank you. We are praying.',
  'prayer.thanksNote':
    'The prayer team is down front before and after every session, if you would rather somebody prayed with you in person.',

  'contact.eyebrow': 'Questions?',
  'contact.heading': 'Let us know',
  'contact.blurb':
    'Registration, dietary needs, childcare, bringing a friend: send it here and the team will answer by email.',

  /** `{name}` is her first name, or "friend" when we do not know it. */
  'hero.live': 'Happening now. Welcome, {name}.',
  'hero.liveCta': "Today's schedule",
} as const;

export type CopyKey = keyof typeof COPY_DEFAULTS;

/** Resolved copy, ready to hand to a client component as a plain object. */
export type Copy = Record<CopyKey, string>;

/** "Sign {name} up" -> "Sign Sarah up". */
export function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole);
}
